import {
  Checkbox,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@gearbox-protocol/deploy-tools-ui";
import type {
  TaskDefinitionFormOverrideSchema,
  TaskDefinitionFormSchema,
} from "api";
import EnvInputs from "components/forms/EnvInputs";
import MultiInput from "components/forms/MultiInput";
import type { FC } from "react";
import type { UseFormReturn } from "react-hook-form";

import TaskDefinitionArtifacts from "./TaskDefinitionArtifacts";

interface TaskDefinitionConstructorProps {
  form: UseFormReturn<Omit<TaskDefinitionFormOverrideSchema, "id">>;
  hideArtifacts?: boolean;
}

export const TaskDefinitionConstructor: FC<
  TaskDefinitionConstructorProps
> = props => {
  const { form, hideArtifacts } = props;

  return (
    <>
      <FormField
        control={form.control}
        name="family"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Task Family</FormLabel>
            <FormControl>
              <Input
                placeholder="Task family (optional)"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Task name</FormLabel>
            <FormControl>
              <Input
                placeholder="Task name, also used as UI tab name (optional)"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Task Description</FormLabel>
            <FormControl>
              <Input
                placeholder="Task description (optional)"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="image"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Docker image</FormLabel>
            <FormControl>
              <Input
                placeholder="Docker image"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="version"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Docker image version</FormLabel>
            <FormControl>
              <Input
                placeholder="Docker image version (optional)"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="entrypoint"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center">
            <FormLabel className="w-24">Entrypoint</FormLabel>
            <FormControl className="ml-2">
              <MultiInput
                field={field}
                className="h-8"
                placeholder="entrypoint"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="command"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center">
            <FormLabel className="w-24">Command</FormLabel>
            <FormControl className="ml-2">
              <MultiInput field={field} className="h-8" placeholder="command" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="env"
        render={({ field }) => (
          <FormItem className="flex flex-row gap-x-1">
            <FormLabel className="w-24 pt-2">Environment variables</FormLabel>

            <EnvInputs field={field} />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="secrets"
        render={({ field }) => (
          <FormItem className="flex flex-row gap-x-1">
            <FormLabel className="w-24 pt-2">Secrets</FormLabel>

            <EnvInputs field={field} secret />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="concurrent"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-2 space-y-0 py-2">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="leading-none">
              <FormLabel>Concurrent</FormLabel>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="multicontainer"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-2 space-y-0 py-2">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="leading-none">
              <FormLabel>Multicontainer</FormLabel>
            </div>
          </FormItem>
        )}
      />

      {!hideArtifacts && (
        <TaskDefinitionArtifacts
          form={form as unknown as UseFormReturn<TaskDefinitionFormSchema>}
        />
      )}
    </>
  );
};
